import { getConnect } from '@genesislcap/foundation-comms';
import { showNotificationToast, showNotificationDialog } from '@genesislcap/foundation-notifications';
import type { GridApi } from '@ag-grid-community/core';
import {
  getEligibleRowsForSubmitAll,
  type SubmitEligibilityRow,
  type EligibleBidToSubmit,
} from './bulk-response-upload/submit-eligibility';
import { NUMERIC_BOND_ID_REGEX } from './bulk-response-upload/upload-validator';
import {
  sanitizeCriteriaString,
  stripTimeTillDueWindowFromCriteria,
  parseDueTimeWindowMs,
  buildDueTimeDateTimeExprRange,
  stripDueWithinFormFieldsFromCriteria,
  stripBidTimeHoursFormFieldsFromCriteria,
  stripFoundationBidTimeColumnCriteria,
} from './bond-bid-criteria-format-utils';

const connect = getConnect();

const MS_PER_HOUR = 60 * 60 * 1000;

/**
 * Sets or replaces `FIRM_FOR` range in criteria (milliseconds). Matches server view:
 * `FIRM_FOR = FIRM_TIME.millis - DUE_TIME.millis` (LONG).
 * User inputs hours; converted to ms for the dataserver query (`MUNICIPAL_BOND_VIEW_TEST`).
 */
export function appendBidTimeMsCriteria(
  base: string,
  minHours: number | null,
  maxHours: number | null
): string {
  let s = stripFoundationBidTimeColumnCriteria(base ?? '');
  let minMs = minHours != null && Number.isFinite(minHours) ? minHours * MS_PER_HOUR : null;
  let maxMs = maxHours != null && Number.isFinite(maxHours) ? maxHours * MS_PER_HOUR : null;
  if (minMs != null && maxMs != null && minMs > maxMs) {
    const t = minMs;
    minMs = maxMs;
    maxMs = t;
  }
  if (minMs == null && maxMs == null) {
    return sanitizeCriteriaString(s);
  }
  const clause =
    minMs != null && maxMs != null
      ? `((FIRM_FOR >= ${minMs} && FIRM_FOR <= ${maxMs}))`
      : minMs != null
        ? `((FIRM_FOR >= ${minMs}))`
        : `((FIRM_FOR <= ${maxMs}))`;
  const combined = s ? `${s} && ${clause}` : clause;
  return sanitizeCriteriaString(combined);
}

export function parseEpochMsFromFilterField(value: unknown): number | null {
  if (value == null || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const asNum = Number(value);
    if (Number.isFinite(asNum)) {
      return asNum;
    }
    const parsed = Date.parse(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
}

/** Hours: (FIRM_TIME − DUE_TIME) / 1h; null if either time is missing/invalid. */
export function bidTimeDeltaHours(firmTime: unknown, dueTime: unknown): number | null {
  const firmMs = parseEpochMsFromFilterField(firmTime);
  const dueMs = parseEpochMsFromFilterField(dueTime);
  if (firmMs == null || dueMs == null) return null;
  return (firmMs - dueMs) / (60 * 60 * 1000);
}

/**
 * Parses `FIRM_FOR_HOURS` fragments from foundation-filters criteria (e.g. >=, <=, ==).
 */
export function extractBidTimeHoursRangeFromCriteria(criteria: string): {
  minHours: number | null;
  maxHours: number | null;
} {
  if (!criteria) return { minHours: null, maxHours: null };

  let minHours: number | null = null;
  let maxHours: number | null = null;
  const regex = /FIRM_FOR_HOURS\s*(>=|<=|>|<|==)\s*(-?\d+(?:\.\d+)?)/g;
  let match: RegExpExecArray | null = null;

  while ((match = regex.exec(criteria)) !== null) {
    const operator = match[1];
    const value = Number(match[2]);
    if (!Number.isFinite(value)) continue;

    if (operator === '==') {
      minHours = value;
      maxHours = value;
    } else if (operator === '>=' || operator === '>') {
      minHours = minHours == null ? value : Math.max(minHours, value);
    } else if (operator === '<=' || operator === '<') {
      maxHours = maxHours == null ? value : Math.min(maxHours, value);
    }
  }

  if (minHours != null && maxHours != null && minHours > maxHours) {
    [minHours, maxHours] = [maxHours, minHours];
  }
  return { minHours, maxHours };
}

/**
 * If criteria produced no range but the form has a single `FIRM_FOR_HOURS` value, treat as exact match (min = max).
 */
export function supplementBidTimeHoursRangeFromFilterData(
  range: { minHours: number | null; maxHours: number | null },
  filterData: Record<string, unknown> | null | undefined
): { minHours: number | null; maxHours: number | null } {
  if (!filterData) return range;
  if (range.minHours != null || range.maxHours != null) return range;
  const raw = filterData.FIRM_FOR_HOURS;
  if (raw == null || raw === '') return range;
  const n = Number(raw);
  if (!Number.isFinite(n)) return range;
  return { minHours: n, maxHours: n };
}

export function rowMatchesBidTimeHoursRange(
  firmTime: unknown,
  dueTime: unknown,
  minHours: number | null,
  maxHours: number | null
): boolean {
  if (minHours == null && maxHours == null) return true;
  const delta = bidTimeDeltaHours(firmTime, dueTime);
  if (delta == null) return false;
  if (minHours != null && maxHours != null) {
    return delta >= minHours && delta <= maxHours;
  }
  if (minHours != null) return delta >= minHours;
  if (maxHours != null) return delta <= maxHours;
  return true;
}

/**
 * Total ms from Apply Filters (hours × ms per hour). No upper cap — business tracking window is separate.
 */
export function dueWithinTotalMsFromFilterData(data: Record<string, unknown> | null | undefined): number {
  if (!data) return 0;
  const rawAmount = data.DUE_WITHIN_AMOUNT;
  if (rawAmount === '' || rawAmount === null || rawAmount === undefined) return 0;
  const amount = parseFloat(String(rawAmount).trim());
  if (!Number.isFinite(amount) || amount <= 0) return 0;
  return amount * 3600000;
}

/**
 * Normalizes Due Within input in-place.
 * Returns true when DUE_WITHIN_AMOUNT was cleared due to invalid input.
 */
export function clearInvalidDueWithinInput(data: Record<string, unknown> | null | undefined): boolean {
  if (!data) return false;
  const rawAmount = data.DUE_WITHIN_AMOUNT;
  if (rawAmount == null || rawAmount === '') return false;

  const amount = Number(String(rawAmount).trim());
  const isInvalidNumeric = !Number.isFinite(amount) || amount <= 0;

  if (isInvalidNumeric) {
    data.DUE_WITHIN_AMOUNT = '';
    return true;
  }
  return false;
}

/** Pull all `CUSIP == '…'` / `"…"` literals from a criteria string (empty values dropped). */
export function extractCusipEqualityValues(criteria: string): string[] {
  const out: string[] = [];
  const re = /CUSIP\s*==\s*["']([^"']*)["']/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(criteria ?? '')) !== null) {
    if (m[1]) out.push(m[1]);
  }
  return out;
}

/** Remove all CUSIP equality OR-groups / singles, then sanitize. */
export function stripCusipEqualityClauses(criteria: string): string {
  return sanitizeCriteriaString(
    (criteria ?? '')
      .replace(/\(CUSIP == ["'][^"']*["']( \|\| CUSIP == ["'][^"']*["'])*\)/g, '')
      .replace(/CUSIP == ["'][^"']*["']/g, '')
  );
}

/** Sorted `(CUSIP == 'a' || CUSIP == 'b')` for stable criteria compare. */
export function buildCusipOrClause(cusips: string[]): string {
  const sorted = [...cusips].filter(Boolean).sort((a, b) => a.localeCompare(b));
  if (!sorted.length) return '';
  const chain = sorted.map((c) => `CUSIP == '${c}'`).join(' || ');
  return sorted.length === 1 ? chain : `(${chain})`;
}

/**
 * Merge base (saved/flyout) CUSIPs with watchlist set.
 * - watchlist null (OFF) → base
 * - base null + watchlist ON → watchlist
 * - both set → intersection (may be [])
 * - both null → null (no CUSIP constraint)
 */
export function mergeWatchlistCusips(
  base: string[] | null,
  watchlist: string[] | null
): string[] | null {
  if (watchlist === null) return base;
  if (base === null) return watchlist;
  const wl = new Set(watchlist);
  return base.filter((c) => wl.has(c));
}

/**
 * Strip existing CUSIP clauses from baseCriteria, then append effective merged clause.
 * Empty intersection → `CUSIP == ''` (force no rows).
 */
export function buildWatchlistMergedCriteria(
  baseCriteria: string,
  base: string[] | null,
  watchlist: string[] | null
): string {
  const nonCusip = stripCusipEqualityClauses(baseCriteria);
  const eff = mergeWatchlistCusips(base, watchlist);
  const clause =
    eff === null ? '' : eff.length ? buildCusipOrClause(eff) : "CUSIP == ''";
  if (!clause) return nonCusip;
  return sanitizeCriteriaString(nonCusip ? `${nonCusip} && ${clause}` : clause);
}

/** Parse flyout/saved-filter CUSIP textarea (comma/newline separated). */
export function cusipsFromFilterData(filterData: Record<string, unknown> | null | undefined): string[] | null {
  if (!filterData || !('CUSIP' in filterData)) return null;
  const raw = filterData.CUSIP;
  if (raw == null || String(raw).trim() === '') return null;
  const values = String(raw)
    .split(/[\n\r,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  return values.length ? values : null;
}

function convertContainsIgnoreCaseMultiValueField(
  field: 'CUSIP' | 'BOND_ID',
  criteria: string
): string {
  // Captures containsIgnoreCase(field, "a,b,c") so comma/newline-separated values can be expanded to OR predicates.
  const containsIgnoreCasePattern = `Expr\\.containsIgnoreCase\\(${field},\\s*["']([^"']+)["']\\)`;
  const containsIgnoreCaseGlobalRegex = new RegExp(containsIgnoreCasePattern, 'g');
  const containsIgnoreCaseSingleRegex = new RegExp(containsIgnoreCasePattern);
  const matches = criteria.match(containsIgnoreCaseGlobalRegex);
  if (!matches) return criteria;

  let nextCriteria = criteria;
  matches.forEach((match) => {
    const innerMatch = match.match(containsIgnoreCaseSingleRegex);
    if (!innerMatch) return;
    const values = innerMatch[1]
      .split(/[\n\r,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    const orChain = values.map((v) => `${field} == "${v}"`).join(' || ');
    const replacement = values.length > 1 ? `(${orChain})` : orChain;
    nextCriteria = nextCriteria.replace(match, replacement);
  });
  return nextCriteria;
}

function hasExplicitDueTimeCriteria(criteria: string): boolean {
  const s = criteria ?? '';
  return (
    /Expr\.dateTimeIsGreaterEqual\(\s*DUE_TIME\s*,/i.test(s) ||
    /Expr\.dateTimeIsLessEqual\(\s*DUE_TIME\s*,/i.test(s) ||
    /\bDUE_TIME\s*>=\s*\d+/i.test(s) ||
    /\bDUE_TIME\s*<=\s*\d+/i.test(s)
  );
}

function escapeRegex(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function removeExactCriteriaClause(criteria: string, clause: string): string {
  if (!criteria || !clause) return criteria ?? '';
  const escaped = escapeRegex(clause);
  let s = criteria;
  s = s.replace(new RegExp(`\\s*&&\\s*${escaped}`, 'g'), '');
  s = s.replace(new RegExp(`${escaped}\\s*&&\\s*`, 'g'), '');
  s = s.replace(new RegExp(`${escaped}`, 'g'), '');
  return sanitizeCriteriaString(s);
}

export interface ApplyBondBiddingFilterInput {
  currentCriteria: string;
  previousCriteria: string;
  filterData: Record<string, unknown> | null;
  applyTimeFiltersNow: boolean;
  currentBidTimeHoursMin: number | null;
  currentBidTimeHoursMax: number | null;
}

export interface ApplyBondBiddingFilterResult {
  criteria: string;
  bidTimeHoursMin: number | null;
  bidTimeHoursMax: number | null;
}

/**
 * Normalizes and applies bond-bidding-specific custom filters produced by foundation-filters.
 * Keeps route code focused on event/UI orchestration.
 */
export function applyBondBiddingFilterCriteria(
  input: ApplyBondBiddingFilterInput
): ApplyBondBiddingFilterResult {
  const totalMs = dueWithinTotalMsFromFilterData(input.filterData);
  let criteriaSource = input.currentCriteria || '';
  // Remove only the previously generated Due Within clause (if present) so we don't strip user-entered DUE_TIME criteria.
  if (totalMs > 0) {
    const previousDueWindow = parseDueTimeWindowMs(input.previousCriteria || '');
    if (previousDueWindow && previousDueWindow.end - previousDueWindow.start === totalMs) {
      const previousGeneratedClause = buildDueTimeDateTimeExprRange(
        previousDueWindow.start,
        previousDueWindow.end
      );
      criteriaSource = removeExactCriteriaClause(criteriaSource, previousGeneratedClause);
    }
  }
  const explicitDueTimeInCurrentCriteria = hasExplicitDueTimeCriteria(criteriaSource);
  let criteria = convertContainsIgnoreCaseMultiValueField('CUSIP', criteriaSource);
  criteria = convertContainsIgnoreCaseMultiValueField('BOND_ID', criteria);
  criteria = stripTimeTillDueWindowFromCriteria(criteria);
  criteria = stripDueWithinFormFieldsFromCriteria(criteria);
  criteria = stripBidTimeHoursFormFieldsFromCriteria(criteria);
  criteria = sanitizeCriteriaString(criteria);

  let nextBidTimeHoursMin = input.currentBidTimeHoursMin;
  let nextBidTimeHoursMax = input.currentBidTimeHoursMax;

  if (input.applyTimeFiltersNow) {
    if (totalMs > 0 && !explicitDueTimeInCurrentCriteria) {
      const preserved = parseDueTimeWindowMs(input.previousCriteria || '');
      const now = Date.now();
      const windowStart = preserved && preserved.end - preserved.start === totalMs ? preserved.start : now;
      const windowEnd = preserved && preserved.end - preserved.start === totalMs ? preserved.end : now + totalMs;
      const windowClause = buildDueTimeDateTimeExprRange(windowStart, windowEnd);
      criteria = criteria ? `${criteria} && ${windowClause}` : windowClause;
      criteria = sanitizeCriteriaString(criteria);
    }

    const bidTimeForm = input.filterData?.['FIRM_FOR'] as { min?: unknown; max?: unknown } | undefined;
    const bidMinRaw = bidTimeForm?.min ?? input.filterData?.['FIRM_FOR_MIN'];
    const bidMaxRaw = bidTimeForm?.max ?? input.filterData?.['FIRM_FOR_MAX'];
    nextBidTimeHoursMin =
      bidMinRaw != null && Number.isFinite(Number(bidMinRaw)) ? Number(bidMinRaw) : null;
    nextBidTimeHoursMax =
      bidMaxRaw != null && Number.isFinite(Number(bidMaxRaw)) ? Number(bidMaxRaw) : null;
    criteria = appendBidTimeMsCriteria(criteria, nextBidTimeHoursMin, nextBidTimeHoursMax);
  } else {
    // Non-search change: preserve previously applied Firm For and Due Within windows as-is.
    // Clearing these form fields without clicking Search must NOT remove applied criteria.
    nextBidTimeHoursMin = input.currentBidTimeHoursMin;
    nextBidTimeHoursMax = input.currentBidTimeHoursMax;

    const preservedDueWindow = parseDueTimeWindowMs(input.previousCriteria || '');
    if (preservedDueWindow && !explicitDueTimeInCurrentCriteria) {
      const preservedDueClause = buildDueTimeDateTimeExprRange(
        preservedDueWindow.start,
        preservedDueWindow.end
      );
      criteria = criteria ? `${criteria} && ${preservedDueClause}` : preservedDueClause;
      criteria = sanitizeCriteriaString(criteria);
    }
    criteria = appendBidTimeMsCriteria(criteria, nextBidTimeHoursMin, nextBidTimeHoursMax);
  }

  return {
    criteria,
    bidTimeHoursMin: nextBidTimeHoursMin,
    bidTimeHoursMax: nextBidTimeHoursMax,
  };
}

/**
 * Fallback used by export path: derive Firm For range in hours from ms criteria when route state
 * min/max are empty.
 */
export function resolveBidTimeHoursRangeForDownload(
  rawCriteria: string,
  currentMin: number | null,
  currentMax: number | null
): { minHours: number | null; maxHours: number | null } {
  if (currentMin != null || currentMax != null) {
    return { minHours: currentMin, maxHours: currentMax };
  }
  const matches = [...(rawCriteria || '').matchAll(/\bFIRM_FOR\s*(>=|<=)\s*([\d.]+)/g)];
  if (matches.length === 0) {
    return { minHours: null, maxHours: null };
  }
  let minMs: number | null = null;
  let maxMs: number | null = null;
  for (const match of matches) {
    const operator = match[1];
    const value = Number(match[2]);
    if (!Number.isFinite(value)) continue;
    if (operator === '>=') minMs = minMs == null ? value : Math.max(minMs, value);
    if (operator === '<=') maxMs = maxMs == null ? value : Math.min(maxMs, value);
  }
  return {
    minHours: minMs != null ? minMs / MS_PER_HOUR : null,
    maxHours: maxMs != null ? maxMs / MS_PER_HOUR : null,
  };
}

// --- Upload: backend-controlled response type count ---

/**
 * Counts how many parsed upload rows have a BID_PRICE_TYPE that differs from the backend's
 * BOND_PRICE_TYPE when the backend has a predefined type (Spread/Yield/Price, not 'None').
 * Those rows will have their response type ignored; the backend value is retained.
 * BOND_ID is treated as numeric-only (consistent with upload validator).
 */
export function countEditedBackendControlledResponseType(
  parsedRows: { BOND_ID?: string; BID_PRICE_TYPE?: string }[],
  backendRowMap: Map<string, Record<string, unknown>>
): number {
  return parsedRows.reduce((count, row) => {
    const bondId = row.BOND_ID?.trim();
    if (!bondId || !NUMERIC_BOND_ID_REGEX.test(bondId)) return count;
    const backendRow = backendRowMap.get(bondId);
    if (!backendRow) return count;
    const bondTypeRaw = (backendRow.BOND_PRICE_TYPE as string) ?? '';
    const bondType = bondTypeRaw.trim();
    if (!bondType || bondType.toLowerCase() === 'none') return count;
    const uploadedTypeRaw = (row.BID_PRICE_TYPE ?? '').trim();
    if (!uploadedTypeRaw) return count;
    const bondTypeLower = bondType.toLowerCase();
    const uploadedTypeLower = uploadedTypeRaw.toLowerCase();
    return uploadedTypeLower !== bondTypeLower ? count + 1 : count;
  }, 0);
}

// --- Submit All helpers (used by bond-bidding route) ---

/**
 * Returns reference IDs from grid filter (BOND_ID set) or foundation filter BOND_ID string.
 * Enforces numeric-only IDs to reduce the risk of injection (SQL/XSS) via user-entered filters.
 */
function getSubmitAllReferenceIds(
  api: GridApi | null | undefined,
  foundationFilterBondId?: string | null
): string[] {
  if (!api) return [];
  const filterModel = api.getFilterModel?.() ?? {};
  const bondIdFilter = filterModel.BOND_ID;

  if (bondIdFilter?.values && Array.isArray(bondIdFilter.values)) {
    return (bondIdFilter.values as string[])
      .map((v) => String(v).trim())
      .filter((v) => NUMERIC_BOND_ID_REGEX.test(v));
  }
  if (foundationFilterBondId != null && foundationFilterBondId !== '') {
    return String(foundationFilterBondId)
      .split(/[\n\r,]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0 && NUMERIC_BOND_ID_REGEX.test(s));
  }
  return [];
}

/**
 * Returns list of row data eligible for Submit All from the grid (uses shared bulk-response-upload eligibility logic).
 */
function getEligibleBidsToSubmitFromGrid(
  api: GridApi | null | undefined,
  referenceIds: string[]
): EligibleBidToSubmit[] {
  if (!api || referenceIds.length === 0) return [];
  const referenceIdSet = new Set(referenceIds);
  const rows: SubmitEligibilityRow[] = [];
  api.forEachNode((node) => {
    const data = node?.data;
    if (!data || !referenceIdSet.has(String(data.BOND_ID ?? ''))) return;
    rows.push(data);
  });
  return getEligibleRowsForSubmitAll(rows, referenceIdSet, Date.now());
}

export interface SubmitAllContext {
  referenceIds: string[];
  eligibleBids: EligibleBidToSubmit[];
}

/**
 * Returns reference IDs and eligible bids in one pass (avoids duplicate grid/filter reads).
 */
export function getSubmitAllContext(
  api: GridApi | null | undefined,
  foundationFilterBondId?: string | null
): SubmitAllContext {
  const referenceIds = getSubmitAllReferenceIds(api, foundationFilterBondId);
  const eligibleBids = getEligibleBidsToSubmitFromGrid(api, referenceIds);
  return { referenceIds, eligibleBids };
}

export type SubmitBondBidResult = { success: true } | { success: false; error: string };

/**
 * Returns the row data to set after a single-row submit attempt.
 * On success: clears _originalBidPrice / _originalBidPriceType so the action cell shows Edit/Delete.
 * On failure: keeps them so hasChanges stays true and the cell keeps showing Submit (e.g. Cancelled order).
 */
export function getRowDataAfterSingleSubmit<T extends Record<string, unknown>>(
  currentRowData: T,
  result: SubmitBondBidResult
): T {
  const base = { ...currentRowData, isEditing: false };
  if (result.success) {
    return { ...base, _originalBidPrice: undefined, _originalBidPriceType: undefined } as T;
  }
  return base as T;
}

export type SubmitBondBidsBatchResult =
  | { success: true; submittedBondIds: string[]; failedBids: Array<{ BOND_ID: string; ERROR: string }> }
  | { success: false; error: string };

function getReplyErrorMessage(reply: { ERROR?: unknown }): string {
  const err = reply.ERROR;
  if (Array.isArray(err)) {
    return err.map((e) => (e && typeof e === 'object' && 'TEXT' in e ? (e as { TEXT?: string }).TEXT : JSON.stringify(e))).filter(Boolean).join('; ') || 'Unknown error';
  }
  return err != null ? String(err) : 'Unknown error';
}

export async function submitBondBid(
  bondId: string,
  bidPrice: string,
  bidPriceType: string,
  options?: { silent?: boolean }
): Promise<SubmitBondBidResult> {
  const silent = options?.silent === true;
  try {
    const reply = await connect.commitEvent(
      'EVENT_BOND_BID_UPSERT',
      {
        DETAILS: {
          BOND_ID: bondId,
          BID_PRICE: bidPrice,
          BID_PRICE_TYPE: bidPriceType,
        }
      }
    );
    if (reply.MESSAGE_TYPE !== 'EVENT_ACK') {
      const errorMsg = getReplyErrorMessage(reply);
      if (!silent) {
        showNotificationToast({
          title: 'Bond Bid Failed',
          body: errorMsg,
          toast: {
            autoClose: true,
            closeTimeout: 3000,
            type: 'error',
          },
        }, 'rapid');
      }
      return { success: false, error: errorMsg };
    }
    return { success: true };
  } catch (error) {
    const errorMsg = error?.message ?? 'Unknown error';
    if (!silent) {
      showNotificationToast({
        title: 'Bond Bid Failed',
        body: errorMsg,
        toast: {
          autoClose: true,
          closeTimeout: 3000,
          type: 'error',
        },
      }, 'rapid');
    }
    return { success: false, error: errorMsg };
  }
}

/**
 * Submit multiple bond bids in a single request. Backend validates and commits each bid;
 * returns which succeeded and which failed (partial success supported).
 */
export async function submitBondBidsBatch(
  bids: Array<{ BOND_ID: string; BID_PRICE: string; BID_PRICE_TYPE: string }>
): Promise<SubmitBondBidsBatchResult> {
  if (bids.length === 0) {
    return { success: true, submittedBondIds: [], failedBids: [] };
  }
  try {
    const reply = await connect.commitEvent('EVENT_BOND_BID_SUBMIT_ALL', {
      DETAILS: {
        BIDS: bids.map((b) => ({
          BOND_ID: b.BOND_ID,
          BID_PRICE: b.BID_PRICE,
          BID_PRICE_TYPE: b.BID_PRICE_TYPE,
        })),
      },
    });
    if (reply.MESSAGE_TYPE !== 'EVENT_ACK') {
      const errorMsg = getReplyErrorMessage(reply);
      return { success: false, error: errorMsg };
    }
    const generated = (reply as { GENERATED?: unknown[] }).GENERATED;
    const first = Array.isArray(generated) ? generated[0] : undefined;
    const payload = first && typeof first === 'object' ? (first as Record<string, unknown>) : {};
    const submittedBondIds = Array.isArray(payload.BIDS_SUBMITTED)
      ? (payload.BIDS_SUBMITTED as string[])
      : [];
    const failedBids = Array.isArray(payload.BIDS_FAILED)
      ? (payload.BIDS_FAILED as Array<{ BOND_ID: string; ERROR: string }>)
      : [];
    return { success: true, submittedBondIds, failedBids };
  } catch (error) {
    const errorMsg = error?.message ?? 'Unknown error';
    return { success: false, error: errorMsg };
  }
}

export async function cancelBondBid(
  bidId: string
): Promise<boolean> {
  const confirmed = await new Promise<boolean>((resolve) => {
    showNotificationDialog({
      title: 'Cancel Bond Bid',
      body: 'Do you want to cancel this bid?',
      dialog: {
        confirmingActions: [{ label: 'Confirm', action: () => resolve(true) }],
        dismissingAction: { label: 'Dismiss', action: () => resolve(false) },
      },
    }, 'rapid');
  });
  if (!confirmed) {
    return false;
  }
  try {
    const reply = await connect.commitEvent(
      'EVENT_BOND_BID_CANCEL',
      {
        DETAILS: {
          BID_ID: bidId,
        }
      }
    );
    if (reply.MESSAGE_TYPE !== 'EVENT_ACK') {
      const errorMsg = Array.isArray(reply.ERROR)
        ? reply.ERROR.map((err) => err.TEXT || JSON.stringify(err)).join('; ')
        : reply.ERROR;
      showNotificationToast({
        title: 'Bond Bid Cancellation Failed',
        body: errorMsg || 'Unknown error',
        toast: {
          autoClose: true,
          closeTimeout: 3000,
          type: 'error',
        },
      }, 'rapid');
      return false;
    }
  } catch (error) {
    showNotificationToast({
      title: 'Bond Bid Cancellation Failed',
      body: error?.message || 'Unknown error',
      toast: {
        autoClose: true,
        closeTimeout: 3000,
        type: 'error',
      },
    }, 'rapid');
    return false;
  }
}

export function formatNumeric(number, numDps = 2, padding = false, locale = navigator.language) {
  if (number === '' || number === null || typeof number === 'undefined') {
    return '';
  }
  return Number(number).toLocaleString(locale, { 
    minimumFractionDigits: padding ? numDps : 0,
    maximumFractionDigits: numDps 
  });
}


export function formatDate(value, locale = navigator.language) {
  if (value) {
    return new Date(value).toLocaleDateString(locale, { timeZone: 'UTC' });
  }
  return '';
}

export function formatTime(value, locale = navigator.language) {
  if (value) {
    return new Date(value).toLocaleTimeString(locale);
  }
  return '';
}

export function formatDateTime(value, locale = navigator.language) {
  if (value) {
    const date = new Date(value);
    const dateFormatted = date.toLocaleDateString(locale);
    const timeFormatted = date.toLocaleTimeString(locale);
    return `${dateFormatted} ${timeFormatted}`;
  }
  return '';
}
